import { RouteError } from "@nexxt/app-kit";
import {
  accountTags,
  elementId,
  emailLocale,
  getCrmTodayInput,
  insightViews,
  invitationFunnelStages,
  linkFilterSteps,
  listCrmAccountsInput,
  sendBuyerFilters,
  sendStatuses,
} from "@nexxt/contracts/crm";
import { signInPageInput } from "@nexxt/contracts/identity";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import * as z from "zod";
import { queryClient } from "./api";
import { meQuery } from "./features/auth/me";
import { lensValue } from "./features/brand/brand-lens";
import { brandTabs } from "./features/brand/brands";
import { sendReportTabs } from "./features/send/report-tabs";
import { GuestlistShell } from "./shell/guestlist-shell";

// The routes of Guestlist, in code (architettura-frontend.md), as Backstage's: the page of a failed login under the
// root, without the user query, so a refused account never loops; everything else inside the shell, which loads the
// user.

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()();

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: signInPageInput,
  component: lazyRouteComponent(() => import("./features/auth/sign-in-page"), "SignInPage"),
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_shell",
  // the lens of the brand, on every screen (ADR-306): a link shows the same brands
  validateSearch: z.object({ brand: lensValue.optional().catch(undefined) }),
  // every screen needs the user; a 401 sends the browser to the login (api.ts)
  beforeLoad: ({ context }) => context.queryClient.ensureQueryData(meQuery),
  component: GuestlistShell,
  errorComponent: RouteError,
});

const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  // the territory of an area manager: none is all of them (B-153), and the link keeps theirs; the appointment whose
  // sheet is open in the panel (ADR-328)
  validateSearch: z.object({
    territory: getCrmTodayInput.shape.territory.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/home/home-page"), "HomePage"),
});

const emailsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/emails",
  // the template open in the panel, the filters of the list, and the dialog of a new email (⌘K opens it)
  validateSearch: z.object({
    panel: z.uuid().optional().catch(undefined),
    new: z.literal(true).optional().catch(undefined),
    q: z.string().trim().min(1).max(100).optional().catch(undefined),
    archived: z.literal(true).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/email-template/emails-page"), "EmailsPage"),
});

const emailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/emails/$templateId",
  // the language, the block, the section or the settings open, and the version looked at: a link shows the same
  validateSearch: z.object({
    locale: emailLocale.optional().catch(undefined),
    block: elementId.optional().catch(undefined),
    section: elementId.optional().catch(undefined),
    settings: z.literal(true).optional().catch(undefined),
    published: z.literal(true).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/email-template/email-page"), "EmailPage"),
});

const sendsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/sends",
  // "Emails · Sent" (ADR-319): the status filter, and the dialog of a new send with the campaign it starts from
  validateSearch: z.object({
    status: z.enum(sendStatuses).optional().catch(undefined),
    campaign: z.uuid().optional().catch(undefined),
    new: z.literal(true).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/send/sends-page"), "SendsPage"),
});

const sendRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/sends/$sendId",
  // the tab of the report, real people or everything recorded (ADR-315 §3), the language of the click map and the
  // list of the buyers (ADR-315 §6)
  validateSearch: z.object({
    tab: z.enum(sendReportTabs).optional().catch(undefined),
    count: z.enum(["real", "all"]).optional().catch(undefined),
    locale: emailLocale.optional().catch(undefined),
    buyers: z.enum(sendBuyerFilters).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/send/send-page"), "SendPage"),
});

/** A link of the place of the kits before P8.14t-t4: the kit open, whose brand's page Brands opens (ADR-306 §9). */
const kitPanel = z.object({ panel: z.uuid().optional().catch(undefined) });

const brandsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/brands",
  // part of the name of a brand, and the kit of an old link
  validateSearch: kitPanel.extend({ q: z.string().trim().min(1).max(100).optional().catch(undefined) }),
  component: lazyRouteComponent(() => import("./features/brand/brands-page"), "BrandsPage"),
});

const brandRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/brands/$brandOrgId",
  // the section open, and the campaign whose buyers it shows: a link shows the same
  validateSearch: z.object({
    tab: z.enum(brandTabs).optional().catch(undefined),
    campaign: z.uuid().optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/brand/brand-page"), "BrandPage"),
});

/** The place of the kits until P8.14t: its links lead to Brands, which opens the page of the kit's brand. */
const kitsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/brand-kits",
  validateSearch: kitPanel,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/brands", search, replace: true });
  },
});

const accountsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/accounts",
  // the filters and the page of the list, and the account open in the panel: an area manager keeps the link of their
  // territory
  validateSearch: z.object({
    panel: z.uuid().optional().catch(undefined),
    q: listCrmAccountsInput.shape.q.catch(undefined),
    kind: listCrmAccountsInput.shape.kind.catch(undefined),
    territory: listCrmAccountsInput.shape.territory.catch(undefined),
    // the tags and the order of the warmth (ADR-316 §1)
    tag: z.enum(accountTags).optional().catch(undefined),
    sort: z.literal("warmth").optional().catch(undefined),
    page: z.coerce.number().int().min(2).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/account/accounts-page"), "AccountsPage"),
});

const accountRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/accounts/$organizationId",
  component: lazyRouteComponent(() => import("./features/account/account-page"), "AccountPage"),
});

const campaignsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns",
  validateSearch: z.object({
    q: z.string().trim().min(1).max(100).optional().catch(undefined),
    archived: z.literal(true).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/campaign/campaigns-page"), "CampaignsPage"),
});

const campaignRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns/$campaignId",
  // the filters and the page of the matrix, the buyer open in the panel, and the dialog open with the buyer it
  // invites (a move of Today, the card of an account): a link shows the same; the brand is the lens's
  validateSearch: z.object({
    q: z.string().trim().min(1).max(100).optional().catch(undefined),
    stage: z.enum(invitationFunnelStages).optional().catch(undefined),
    // how far the booking link of the buyers went (ADR-313 §4): one filter or the stage, never both
    link: z.enum(linkFilterSteps).optional().catch(undefined),
    page: z.coerce.number().int().min(2).optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
    dialog: z.enum(["invite", "links"]).optional().catch(undefined),
    guest: z.uuid().optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/campaign/campaign-page"), "CampaignPage"),
});

const brandReportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/campaigns/$campaignId/brands/$brandOrgId/report",
  component: lazyRouteComponent(() => import("./features/brand-report/brand-report-page"), "BrandReportPage"),
});

const appointmentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/appointments/$appointmentId",
  component: lazyRouteComponent(() => import("./features/appointment/appointment-page"), "AppointmentPage"),
});

const documentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/documents/$documentId",
  component: lazyRouteComponent(() => import("./features/document/document-page"), "DocumentPage"),
});

const insightsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/insights",
  // the view, the season and the territory (ADR-315 §5), and the cell of Brands × territories open: a link shows the same
  validateSearch: z.object({
    view: z.enum(insightViews).optional().catch(undefined),
    season: z.uuid().optional().catch(undefined),
    territory: listCrmAccountsInput.shape.territory.catch(undefined),
    cellBrand: z.uuid().optional().catch(undefined),
    cellTerritory: listCrmAccountsInput.shape.territory.catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/insights/insights-page"), "InsightsPage"),
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  shellRoute.addChildren([
    homeRoute,
    accountsRoute,
    accountRoute,
    campaignsRoute,
    campaignRoute,
    brandReportRoute,
    appointmentRoute,
    documentRoute,
    emailsRoute,
    emailRoute,
    sendsRoute,
    sendRoute,
    brandsRoute,
    brandRoute,
    kitsRoute,
    insightsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultPendingMs: 300,
  defaultViewTransition: true,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
